import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { switchMap, map, catchError, concatMap } from 'rxjs/operators';
import { of } from 'rxjs';
import * as ErrorActions from 'src/app/store/global-notification/actions/global-notification.action';
import * as ReportsActions from '../actions/reports.actions';
import { ReportsActionTypes } from '../actions/reports.actions';
import { ReportsService } from 'src/app/services/http/reports.service';
import { parseError } from 'src/app/utils/error-parse';

@Injectable()
export class ReportsEffects {

  constructor(private actions$: Actions, private reportsService: ReportsService) { }

  downloadReport$ = createEffect(() => this.actions$.pipe(
    ofType(ReportsActionTypes.DOWNLOAD_REPORT),
    switchMap((action: ReportsActions.DownloadReport) => {
      return this.reportsService.downloadReport(action.payload)
        .pipe(
          switchMap((res: any) => {
            let fileName = res.headers.get('content-disposition').split('filename=')[1];
            fileName = fileName.replace(/['"]+/g, '');
            const fileType = res.headers.get('content-type');
            return this.reportsService.writeFileFromBlob(res.body, fileType, fileName)
              .pipe(
                concatMap((resFile: any) => {
                  return [
                    new ReportsActions.RunReportSuccess('success!')
                  ];
                })
              );
          })
          , catchError((err) => {
            const errorText = parseError(err, "Error running report");
            return of(
              new ErrorActions.NotificationErrorAction(errorText),
              new ReportsActions.RunReportDataError(errorText)
            );
          })
        );
    })
  ));

  retrieveDocumentsList$ = createEffect(() => this.actions$.pipe(
    ofType(ReportsActionTypes.RETRIEVE_DOCUMENTS_LIST),
    switchMap((action: ReportsActions.RetrieveDocumentsList) => {
      return this.reportsService.retrieveDocumentList(action.payload)
        .pipe(
          map((res: any) => {
            // sort the returned list and reverse the result so that the first document in the list is the last rendered
            const documentListSorted = res.sort((a, b) => (a.myDocumentId > b.myDocumentId) ? 1 : -1).reverse();
            return new ReportsActions.RetrieveDocument(documentListSorted[0]);
          })
          , catchError((err) => {
            const errorText = parseError(err, "Reports list could not be retrieved");
            return of(
              new ErrorActions.NotificationErrorAction(errorText),
              new ReportsActions.RunReportDataError(errorText)
            );
          })
        );
    })
  ));

  retrieveDocument$ = createEffect(() => this.actions$.pipe(
    ofType(ReportsActionTypes.RETRIEVE_DOCUMENT),
    switchMap((action: ReportsActions.RetrieveDocument) => {
      return this.reportsService.retrieveDocument(action.payload)
        .pipe(
          map((res: any) => {
            return new ReportsActions.WritePDFFromBlob(res);
          })
          , catchError((err) => {
            const errorText = parseError(err, "Reports list could not be retrieved");
            return of(
              new ErrorActions.NotificationErrorAction(errorText),
              new ReportsActions.RunReportDataError(errorText)
            );
          })
        );
    })
  ));

  writePdfFromBlob$ = createEffect(() => this.actions$.pipe(
    ofType(ReportsActionTypes.WRITE_PDF_FROM_BLOB),
    switchMap((action: ReportsActions.WritePDFFromBlob) => {
      return this.reportsService.writePdfFromBlob(action.payload)
        .pipe(
          map((res: any) => {
            return new ReportsActions.RunReportSuccess('success!');
          })
          , catchError((err) => {
            const errorText = parseError(err, "PDF could not be written");
            return of(
              new ErrorActions.NotificationErrorAction(errorText),
              new ReportsActions.RunReportDataError(errorText)
            );
          })
        );
    })
  ));

  writePdfFromDocument$ = createEffect(() => this.actions$.pipe(
    ofType(ReportsActionTypes.WRITE_PDF_FROM_DOCUMENT),
    switchMap((action: ReportsActions.WritePDFFromDocument) => {
      return this.reportsService.writePdfFromDocument(action.payload)
        .pipe(
          map((res: any) => {
            return new ReportsActions.RunReportSuccess('success!');
          })
          , catchError((err) => {
            const errorText = parseError(err, "PDF could not be written");
            return of(
              new ErrorActions.NotificationErrorAction(errorText),
              new ReportsActions.RunReportDataError(errorText)
            );
          })
        );
    })
  ));
}
